import type { SocialProvider } from "@artha/types";
import {
  BeehiivIcon,
  InstagramIcon,
  LinkedInIcon,
  SubstackIcon,
  TikTokIcon,
  WhiteTikTokIcon,
  YouTubeIcon,
} from "@/components/icons/SocialIcons";
import YouTubeShareIcon from "@/components/icons/ShareSocialIcon/youTubeIcon";
import { MediumIcon, WebsiteIcon } from "@/components/icons/SocialIcons";

type CreatorSourceConfig = {
  labelKey: string;
  placeholderKey: string;
  inputIcon: React.ComponentType<{ className?: string }>;
  inputIconClassName: string;
  connectIcon: React.ComponentType<{ className?: string }>;
  connectIconClassName: string;
  estimateIcon: React.ComponentType<{ className?: string }>;
  estimateIconClassName: string;
  buildProfileIconClassName: string;
  displayName: string;
};

export type OnboardingCreatorSource = Extract<
  SocialProvider,
  | "linkedin"
  | "instagram"
  | "tiktok"
  | "youtube"
  | "substack"
  | "beehiiv"
  | "medium"
  | "website"
>;

export const CREATOR_SOURCE_CONFIG: Record<
  OnboardingCreatorSource,
  CreatorSourceConfig
> = {
  linkedin: {
    labelKey: "linkedin",
    placeholderKey: "placeholderLinkedin",
    inputIcon: LinkedInIcon,
    inputIconClassName: "size-5 bg-[#0A66C2] text-white rounded p-0.5",
    connectIcon: LinkedInIcon,
    connectIconClassName: "w-5 h-5 text-white",
    estimateIcon: LinkedInIcon,
    estimateIconClassName: "bg-button-primary text-white w-5 h-5 rounded-sm",
    buildProfileIconClassName: "bg-[#0A66C2] text-white rounded p-0.5",
    displayName: "LinkedIn",
  },
  instagram: {
    labelKey: "instagram",
    placeholderKey: "placeholderInstagram",
    inputIcon: InstagramIcon,
    inputIconClassName: "size-5 text-[#C13584]",
    connectIcon: InstagramIcon,
    connectIconClassName: "w-5 h-5 text-white",
    estimateIcon: InstagramIcon,
    estimateIconClassName: "w-5 h-5 text-[#E4405F]",
    buildProfileIconClassName:
      "bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white rounded p-0.5",
    displayName: "Instagram",
  },
  tiktok: {
    labelKey: "tiktok",
    placeholderKey: "placeholderTiktok",
    inputIcon: TikTokIcon,
    inputIconClassName: "size-5 bg-black rounded p-0.5",
    connectIcon: WhiteTikTokIcon,
    connectIconClassName: "w-5 h-5",
    estimateIcon: TikTokIcon,
    estimateIconClassName: "w-5 h-5",
    buildProfileIconClassName: "bg-black text-white rounded p-0.5",
    displayName: "TikTok",
  },
  youtube: {
    labelKey: "youtube",
    placeholderKey: "placeholderYoutube",
    inputIcon: YouTubeShareIcon,
    inputIconClassName: "size-5 text-[#FF0000]",
    connectIcon: YouTubeIcon,
    connectIconClassName: "w-5 h-5",
    estimateIcon: YouTubeIcon,
    estimateIconClassName: "w-5 h-5",
    buildProfileIconClassName: "bg-[#FF0000] text-white rounded p-0.5",
    displayName: "YouTube",
  },
  substack: {
    labelKey: "substack",
    placeholderKey: "placeholderSubstack",
    inputIcon: SubstackIcon,
    inputIconClassName: "size-5 text-[#FE4800]",
    connectIcon: SubstackIcon,
    connectIconClassName: "w-5 h-5 text-white",
    estimateIcon: SubstackIcon,
    estimateIconClassName: "w-5 h-5 text-[#FE4800]",
    buildProfileIconClassName: "text-[#FE4800]",
    displayName: "Substack",
  },
  beehiiv: {
    labelKey: "beehiiv",
    placeholderKey: "placeholderBeehiiv",
    inputIcon: BeehiivIcon,
    inputIconClassName: "size-5 text-black",
    connectIcon: BeehiivIcon,
    connectIconClassName: "w-5 h-5 text-black",
    estimateIcon: BeehiivIcon,
    estimateIconClassName: "w-5 h-5 text-black",
    buildProfileIconClassName: "text-black",
    displayName: "Beehiiv",
  },
  medium: {
    labelKey: "medium",
    placeholderKey: "placeholderMedium",
    inputIcon: MediumIcon,
    inputIconClassName: "size-5 text-black",
    connectIcon: MediumIcon,
    connectIconClassName: "w-5 h-5 text-white",
    estimateIcon: MediumIcon,
    estimateIconClassName: "w-5 h-5 text-black",
    buildProfileIconClassName: "text-black",
    displayName: "Medium",
  },
  website: {
    labelKey: "website",
    placeholderKey: "placeholderWebsite",
  inputIcon: WebsiteIcon,
  inputIconClassName: "size-5 text-[#2EB67D]",
  connectIcon: WebsiteIcon,
  connectIconClassName: "w-5 h-5 text-white",
  estimateIcon: WebsiteIcon,
    estimateIconClassName: "w-5 h-5 text-[#2EB67D]",
    buildProfileIconClassName: "text-[#2EB67D]",
    displayName: "Website",
  },
};

export const ESTIMATE_EARNINGS_SECONDARY_SOURCE_IDS = [
  "linkedin",
  "instagram",
  "tiktok",
] as const satisfies readonly OnboardingCreatorSource[];

export const ESTIMATE_EARNINGS_MORE_SOURCE_IDS = [
  "substack",
  "beehiiv",
  "medium",
  "website",
] as const satisfies readonly OnboardingCreatorSource[];

export const ESTIMATE_EARNINGS_PRIMARY_SOURCE_ID =
  "youtube" as const satisfies OnboardingCreatorSource;

export const CONNECT_ACCOUNT_SOURCE_IDS = [
  "linkedin",
  "instagram",
  "youtube",
  "tiktok",
  "substack",
  "beehiiv",
  "medium",
  "website",
] as const satisfies readonly OnboardingCreatorSource[];

export const ADD_ACCOUNT_SOURCE_IDS = [
  "linkedin",
  "instagram",
  "tiktok",
  "youtube",
  "substack",
  "beehiiv",
  "medium",
  "website",
] as const satisfies readonly OnboardingCreatorSource[];

export function isOnboardingCreatorSource(
  value: string | null | undefined
): value is OnboardingCreatorSource {
  return !!value && value in CREATOR_SOURCE_CONFIG;
}

export function getCreatorSourceConfig(
  value: string | null | undefined
): CreatorSourceConfig | undefined {
  if (!isOnboardingCreatorSource(value)) {
    return undefined;
  }

  return CREATOR_SOURCE_CONFIG[value];
}
